import React, { useMemo } from "react";
import PropTypes from "prop-types";
import ButtonBlock from "../../../components/Preview/Draggable/Block/ButtonBlock";
import { useApp } from "../../../context/AppState";
import {
  getButtonDisplay,
  BUTTON_CTA_VARIANT,
} from "../../../../shared/product-block-display.js";
import {
  resolveCatalogCtaCssPatch,
  resolvePopupThemeTokens,
} from "../../../utils/resolve-popup-theme-tokens.js";
import styles from "./AiCatalogCtaButton.module.css";

function normalizeCatalogCtaSection(section, popupData, popupState) {
  if (!section) return section;
  const display = getButtonDisplay(section);
  const variant = display.variant || BUTTON_CTA_VARIANT.FILLED_DARK;
  const activeStep =
    typeof popupState?.activeStep === "number" && !Number.isNaN(popupState.activeStep)
      ? popupState.activeStep
      : 0;
  const themeTokens = resolvePopupThemeTokens(popupData, { activeStep });
  const customCSS = section.setting?.customCSS || {};
  const patchDesktop = { ...(customCSS.desktop || {}) };
  const patchMobile = { ...(customCSS.mobile || {}) };

  const isPrimary =
    section?.setting?.role !== "secondaryCta" &&
    variant === BUTTON_CTA_VARIANT.FILLED_DARK;
  const themed = resolveCatalogCtaCssPatch(section, themeTokens, {
    primary: isPrimary,
  });

  // Theme fills gaps only — never overwrite merchant Design tab values.
  Object.keys(themed.desktop || {}).forEach((key) => {
    if (patchDesktop[key] == null || patchDesktop[key] === "") {
      patchDesktop[key] = themed.desktop[key];
    }
  });
  Object.keys(themed.mobile || {}).forEach((key) => {
    if (patchMobile[key] == null || patchMobile[key] === "") {
      patchMobile[key] = themed.mobile[key];
    }
  });

  const hiddenDesktop = customCSS.desktop?.display === "none";
  const hiddenMobile = customCSS.mobile?.display === "none";
  if (hiddenDesktop) patchDesktop.display = "none";
  if (hiddenMobile) patchMobile.display = "none";

  const fallbackRadius = `${themeTokens.buttonRadiusPx}px`;
  if (patchDesktop.borderRadius == null || patchDesktop.borderRadius === "") {
    patchDesktop.borderRadius = fallbackRadius;
  }
  if (patchMobile.borderRadius == null || patchMobile.borderRadius === "") {
    patchMobile.borderRadius = patchDesktop.borderRadius;
  }

  return {
    ...section,
    setting: {
      ...(section.setting || {}),
      layoutFlow: true,
      btn_type: section.setting?.btn_type || { type: "text" },
      customCSS: {
        desktop: {
          ...patchDesktop,
          width: display.fullWidth !== false ? "100%" : patchDesktop.width,
        },
        mobile: {
          ...patchDesktop,
          ...patchMobile,
          width: display.fullWidth !== false ? "100%" : patchMobile.width,
        },
      },
    },
  };
}

export default function AiCatalogCtaButton(props) {
  const { appState } = useApp();
  const section = useMemo(
    () => normalizeCatalogCtaSection(props.section, props.popupData, props.popupState),
    [props.section, props.popupData, props.popupState]
  );

  const isSecondary =
    getButtonDisplay(section).variant === BUTTON_CTA_VARIANT.OUTLINE_MUTED ||
    section?.setting?.role === "secondaryCta";
  const isEditorPreview = Boolean(appState?.is_preview_mode);
  const desktopCss = section?.setting?.customCSS?.desktop || {};
  const hasDeclineChrome =
    isSecondary &&
    ((desktopCss.backgroundColor &&
      desktopCss.backgroundColor !== "transparent") ||
      (Number.parseFloat(desktopCss.borderWidth) || 0) > 0 ||
      (Number.parseFloat(desktopCss.borderRadius) || 0) > 0);

  return (
    <div
      className={`${styles.wrap}${
        isSecondary && !hasDeclineChrome ? ` ${styles.wrapSecondary}` : ""
      }`.trim()}
      style={
        isEditorPreview
          ? { pointerEvents: "none", cursor: "default" }
          : undefined
      }
      aria-disabled={isEditorPreview ? "true" : undefined}
    >
      <ButtonBlock
        {...props}
        section={section}
        productIndex={props.productIndex}
        productData={props.productData}
      />
    </div>
  );
}

AiCatalogCtaButton.propTypes = {
  section: PropTypes.object,
  popupData: PropTypes.object,
  popupState: PropTypes.object,
  productIndex: PropTypes.number,
  productData: PropTypes.object,
};
